import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  CircularProgress,
  InputAdornment,
  FormHelperText,
  Divider
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useNavigate } from 'react-router-dom';
import Alert from '@material-ui/lab/Alert';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import SaveIcon from '@material-ui/icons/Save';
import { firestore } from '../../firebase/config';
import { getDocs, collection } from 'firebase/firestore';
import commissionService from '../../services/commissionService';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    padding: theme.spacing(3),
  },
  form: {
    width: '100%',
    marginTop: theme.spacing(2),
  },
  formControl: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  buttons: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(3),
  },
  button: {
    marginLeft: theme.spacing(1),
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  divider: {
    margin: theme.spacing(3, 0),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
  }
}));

const CommissionCreatePage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  const [formData, setFormData] = useState({
    partnerId: '',
    partnerName: '',
    tripId: '',
    tripReference: '',
    bookingId: '',
    bookingReference: '',
    amount: '',
    currency: 'EUR',
    status: 'pending',
    description: '',
  });
  
  const [errors, setErrors] = useState({});
  const [partners, setPartners] = useState([]);
  const [trips, setTrips] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loadingPartners, setLoadingPartners] = useState(false);
  const [loadingTrips, setLoadingTrips] = useState(false);
  const [loadingBookings, setLoadingBookings] = useState(false);

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    setLoadingPartners(true);
    try {
      // Simulation de récupération des données depuis Firestore
      // Dans une implémentation réelle, cela serait remplacé par un appel à l'API
      const partnersCollection = collection(firestore, 'partners');
      const partnersSnapshot = await getDocs(partnersCollection);
      
      // Si la collection n'existe pas encore ou est vide, utiliser des données de démonstration
      if (partnersSnapshot.empty) {
        const demoPartners = [
          { 
            id: 'P001', 
            name: 'Entreprise ABC',
            email: 'contact@entrepriseabc.com',
            phone: '+33123456789',
            address: '123 Rue de Paris, 75001 Paris',
            status: 'active'
          },
          { 
            id: 'P002', 
            name: 'Transport Express',
            email: 'info@transportexpress.fr',
            phone: '+33987654321',
            address: '45 Avenue des Champs-Élysées, 75008 Paris',
            status: 'active'
          },
          { 
            id: 'P003', 
            name: 'Voyages Confort',
            email: 'reservations@voyagesconfort.fr',
            phone: '+33678901234',
            address: '78 Boulevard Haussmann, 75009 Paris',
            status: 'active'
          }
        ];
        setPartners(demoPartners);
      } else {
        const fetchedPartners = partnersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setPartners(fetchedPartners);
      }
    } catch (error) {
      console.error('Error fetching partners:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la récupération des partenaires',
        severity: 'error'
      });
    } finally {
      setLoadingPartners(false);
    }
  };

  const fetchTrips = async (partnerId) => {
    if (!partnerId) return;
    
    setLoadingTrips(true);
    try {
      // Simulation de récupération des données depuis Firestore
      // Dans une implémentation réelle, cela serait remplacé par un appel à l'API filtré par partnerId
      const tripsCollection = collection(firestore, 'trips');
      const tripsSnapshot = await getDocs(tripsCollection);
      
      // Si la collection n'existe pas encore ou est vide, utiliser des données de démonstration
      if (tripsSnapshot.empty) {
        const demoTrips = [
          { 
            id: 'T001', 
            reference: 'VOY-2025-001',
            partnerId: 'P001',
            origin: 'Paris',
            destination: 'Lyon',
            departureDate: '2025-04-15T08:00:00',
            status: 'completed'
          },
          { 
            id: 'T002', 
            reference: 'VOY-2025-002',
            partnerId: 'P002',
            origin: 'Marseille',
            destination: 'Nice',
            departureDate: '2025-04-10T10:30:00',
            status: 'completed'
          },
          { 
            id: 'T003', 
            reference: 'VOY-2025-003',
            partnerId: 'P001',
            origin: 'Lyon',
            destination: 'Grenoble',
            departureDate: '2025-04-20T14:15:00',
            status: 'scheduled'
          },
          { 
            id: 'T004', 
            reference: 'VOY-2025-004',
            partnerId: 'P003',
            origin: 'Paris',
            destination: 'Bordeaux',
            departureDate: '2025-04-05T07:45:00',
            status: 'completed'
          },
          { 
            id: 'T005', 
            reference: 'VOY-2025-005',
            partnerId: 'P002',
            origin: 'Lille',
            destination: 'Paris',
            departureDate: '2025-04-18T16:30:00',
            status: 'cancelled'
          }
        ];
        // Filtrer les voyages par partenaire
        const filteredTrips = demoTrips.filter(trip => trip.partnerId === partnerId);
        setTrips(filteredTrips);
      } else {
        const fetchedTrips = tripsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        // Filtrer les voyages par partenaire
        const filteredTrips = fetchedTrips.filter(trip => trip.partnerId === partnerId);
        setTrips(filteredTrips);
      }
    } catch (error) {
      console.error('Error fetching trips:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la récupération des voyages',
        severity: 'error'
      });
    } finally {
      setLoadingTrips(false);
    }
  };

  const fetchBookings = async (tripId) => {
    if (!tripId) return;
    
    setLoadingBookings(true);
    try {
      // Simulation de récupération des données depuis Firestore
      // Dans une implémentation réelle, cela serait remplacé par un appel à l'API filtré par tripId
      const bookingsCollection = collection(firestore, 'bookings');
      const bookingsSnapshot = await getDocs(bookingsCollection);
      
      // Si la collection n'existe pas encore ou est vide, utiliser des données de démonstration
      if (bookingsSnapshot.empty) {
        const demoBookings = [
          { 
            id: 'B001', 
            reference: 'RES-2025-001',
            tripId: 'T001',
            userId: 'U001',
            userName: 'Alice Martin',
            passengerCount: 2,
            totalAmount: 120.50,
            status: 'confirmed'
          },
          { 
            id: 'B002', 
            reference: 'RES-2025-002',
            tripId: 'T002',
            userId: 'U002',
            userName: 'Thomas Bernard',
            passengerCount: 1,
            totalAmount: 85.75,
            status: 'confirmed'
          },
          { 
            id: 'B003', 
            reference: 'RES-2025-003',
            tripId: 'T003',
            userId: 'U003',
            userName: 'Sophie Dubois',
            passengerCount: 3,
            totalAmount: 145.20,
            status: 'pending'
          },
          { 
            id: 'B004', 
            reference: 'RES-2025-004',
            tripId: 'T004',
            userId: 'U004',
            userName: 'Pierre Lambert',
            passengerCount: 2,
            totalAmount: 175.00,
            status: 'confirmed'
          },
          { 
            id: 'B005', 
            reference: 'RES-2025-005',
            tripId: 'T005',
            userId: 'U005',
            userName: 'Julie Moreau',
            passengerCount: 1,
            totalAmount: 65.30,
            status: 'cancelled'
          }
        ];
        // Filtrer les réservations par voyage
        const filteredBookings = demoBookings.filter(booking => booking.tripId === tripId);
        setBookings(filteredBookings);
      } else {
        const fetchedBookings = bookingsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        // Filtrer les réservations par voyage
        const filteredBookings = fetchedBookings.filter(booking => booking.tripId === tripId);
        setBookings(filteredBookings);
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la récupération des réservations',
        severity: 'error'
      });
    } finally {
      setLoadingBookings(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Logique spéciale pour certains champs
    if (name === 'partnerId') {
      const selectedPartner = partners.find(partner => partner.id === value);
      setFormData({
        ...formData,
        partnerId: value,
        partnerName: selectedPartner ? selectedPartner.name : '',
        tripId: '',
        tripReference: '',
        bookingId: '',
        bookingReference: ''
      });
      
      // Charger les voyages associés à ce partenaire
      fetchTrips(value);
      setTrips([]);
      setBookings([]);
    } else if (name === 'tripId') {
      const selectedTrip = trips.find(trip => trip.id === value);
      setFormData({
        ...formData,
        tripId: value,
        tripReference: selectedTrip ? selectedTrip.reference : '',
        bookingId: '',
        bookingReference: ''
      });
      
      // Charger les réservations associées à ce voyage
      fetchBookings(value);
      setBookings([]);
    } else if (name === 'bookingId') {
      const selectedBooking = bookings.find(booking => booking.id === value);
      setFormData({
        ...formData,
        bookingId: value,
        bookingReference: selectedBooking ? selectedBooking.reference : ''
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
    
    // Effacer l'erreur lorsque l'utilisateur modifie le champ
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const handleCancel = () => {
    navigate('/admin/commissions');
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.partnerId) {
      newErrors.partnerId = 'Le partenaire est requis';
    }
    
    if (!formData.tripId) {
      newErrors.tripId = 'Le voyage est requis';
    }
    
    if (!formData.bookingId) {
      newErrors.bookingId = 'La réservation est requise';
    }
    
    if (!formData.amount) {
      newErrors.amount = 'Le montant est requis';
    } else if (isNaN(formData.amount) || parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'Le montant doit être un nombre positif';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'La description est requise';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setSnackbar({
        open: true,
        message: 'Veuillez corriger les erreurs dans le formulaire',
        severity: 'error'
      });
      return;
    }
    
    setSubmitting(true);
    
    try {
      // Préparer les données pour l'envoi
      const commissionData = {
        ...formData,
        amount: parseFloat(formData.amount)
      };
      
      // Créer la commission via le service
      await commissionService.createCommission(commissionData);
      
      setSnackbar({
        open: true,
        message: 'Commission créée avec succès',
        severity: 'success'
      });
      
      // Rediriger vers la liste des commissions après un court délai
      setTimeout(() => {
        navigate('/admin/commissions');
      }, 1500);
      
    } catch (error) {
      console.error('Error creating commission:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la création de la commission',
        severity: 'error'
      });
      setSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Générer un ID de commission automatiquement
  const generateCommissionId = () => {
    const prefix = 'COM';
    const randomNum = Math.floor(1000 + Math.random() * 9000);
    return `${prefix}${randomNum}`;
  };

  return (
    <Box className={classes.root}>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<ArrowBackIcon />}
        onClick={handleCancel}
        style={{ marginBottom: 16 }}
      >
        Retour à la liste
      </Button>
      
      <Typography variant="h4" component="h1" gutterBottom>
        Créer une nouvelle commission
      </Typography>
      
      <Paper className={classes.paper}>
        {loading ? (
          <div className={classes.loading}>
            <CircularProgress />
          </div>
        ) : (
          <form className={classes.form} onSubmit={handleSubmit}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Informations de base
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl variant="outlined" className={classes.formControl} error={!!errors.partnerId}>
                  <InputLabel id="partner-label">Partenaire</InputLabel>
                  <Select
                    labelId="partner-label"
                    id="partnerId"
                    name="partnerId"
                    value={formData.partnerId}
                    onChange={handleChange}
                    label="Partenaire"
                    disabled={loadingPartners}
                  >
                    <MenuItem value="">
                      <em>Sélectionner un partenaire</em>
                    </MenuItem>
                    {partners.map(partner => (
                      <MenuItem key={partner.id} value={partner.id}>{partner.name}</MenuItem>
                    ))}
                  </Select>
                  {loadingPartners && <CircularProgress size={24} style={{ position: 'absolute', right: 10, top: 10 }} />}
                  {errors.partnerId && <FormHelperText>{errors.partnerId}</FormHelperText>}
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl variant="outlined" className={classes.formControl} error={!!errors.status}>
                  <InputLabel id="status-label">Statut</InputLabel>
                  <Select
                    labelId="status-label"
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    label="Statut"
                  >
                    <MenuItem value="pending">En attente</MenuItem>
                    <MenuItem value="paid">Payée</MenuItem>
                    <MenuItem value="cancelled">Annulée</MenuItem>
                  </Select>
                  {errors.status && <FormHelperText>{errors.status}</FormHelperText>}
                </FormControl>
              </Grid>
            </Grid>
            
            <Divider className={classes.divider} />
            
            <Typography variant="h6" className={classes.sectionTitle}>
              Détails du voyage et de la réservation
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl variant="outlined" className={classes.formControl} error={!!errors.tripId}>
                  <InputLabel id="trip-label">Voyage</InputLabel>
                  <Select
                    labelId="trip-label"
                    id="tripId"
                    name="tripId"
                    value={formData.tripId}
                    onChange={handleChange}
                    label="Voyage"
                    disabled={!formData.partnerId || loadingTrips}
                  >
                    <MenuItem value="">
                      <em>Sélectionner un voyage</em>
                    </MenuItem>
                    {trips.map(trip => (
                      <MenuItem key={trip.id} value={trip.id}>
                        {trip.reference} - {trip.origin} → {trip.destination}
                      </MenuItem>
                    ))}
                  </Select>
                  {loadingTrips && <CircularProgress size={24} style={{ position: 'absolute', right: 10, top: 10 }} />}
                  {errors.tripId && <FormHelperText>{errors.tripId}</FormHelperText>}
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl variant="outlined" className={classes.formControl} error={!!errors.bookingId}>
                  <InputLabel id="booking-label">Réservation</InputLabel>
                  <Select
                    labelId="booking-label"
                    id="bookingId"
                    name="bookingId"
                    value={formData.bookingId}
                    onChange={handleChange}
                    label="Réservation"
                    disabled={!formData.tripId || loadingBookings}
                  >
                    <MenuItem value="">
                      <em>Sélectionner une réservation</em>
                    </MenuItem>
                    {bookings.map(booking => (
                      <MenuItem key={booking.id} value={booking.id}>
                        {booking.reference} - {booking.userName} ({booking.passengerCount} passager{booking.passengerCount > 1 ? 's' : ''})
                      </MenuItem>
                    ))}
                  </Select>
                  {loadingBookings && <CircularProgress size={24} style={{ position: 'absolute', right: 10, top: 10 }} />}
                  {errors.bookingId && <FormHelperText>{errors.bookingId}</FormHelperText>}
                </FormControl>
              </Grid>
            </Grid>
            
            <Divider className={classes.divider} />
            
            <Typography variant="h6" className={classes.sectionTitle}>
              Détails financiers
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  name="amount"
                  label="Montant"
                  variant="outlined"
                  fullWidth
                  required
                  type="number"
                  value={formData.amount}
                  onChange={handleChange}
                  error={!!errors.amount}
                  helperText={errors.amount}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">€</InputAdornment>,
                  }}
                  inputProps={{
                    min: 0.01,
                    step: 0.01
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl variant="outlined" className={classes.formControl}>
                  <InputLabel id="currency-label">Devise</InputLabel>
                  <Select
                    labelId="currency-label"
                    id="currency"
                    name="currency"
                    value={formData.currency}
                    onChange={handleChange}
                    label="Devise"
                  >
                    <MenuItem value="EUR">Euro (€)</MenuItem>
                    <MenuItem value="USD">Dollar américain ($)</MenuItem>
                    <MenuItem value="GBP">Livre sterling (£)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  name="description"
                  label="Description"
                  variant="outlined"
                  fullWidth
                  required
                  value={formData.description}
                  onChange={handleChange}
                  error={!!errors.description}
                  helperText={errors.description}
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>
            
            <div className={classes.buttons}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleCancel}
                className={classes.button}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                className={classes.button}
                disabled={submitting}
                startIcon={submitting ? <CircularProgress size={24} /> : <SaveIcon />}
              >
                {submitting ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </div>
          </form>
        )}
      </Paper>
      
      {/* Snackbar pour les notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CommissionCreatePage;
